<script lang="ts">
	import videojs from "video.js";
	import "video.js/dist/video-js.min.css";
	import { onDestroy, onMount } from "svelte";
	import type Player from "video.js/dist/types/player";

	/**
	 * The Video.js player instance.
	 * @type {Player}
	 */
	let player: Player;

	/**
	 * The source URL of the video.
	 * @type {string}
	 */
	export let videoSrc;

	/**
	 * On component mount, initialize the Video.js player and set up event listeners.
	 */
	onMount(async () => {
		player = await videojs(playerRef, { fluid: true });

		// Adjust the player size based on the video's aspect ratio.
		player.on("loadedmetadata", function () {
			const videoAspectRatio = player.videoWidth() / player.videoHeight();

			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			// Calculate the maximum width and height for the player.
			let maxWidth, maxHeight;

			if (videoAspectRatio > 1) {
				// Landscape video
				maxWidth = Math.min(viewportWidth, 800) - 48; // Limit max width (e.g., 800px or viewport width)
				maxHeight = maxWidth / videoAspectRatio;
			} else {
				// Portrait video
				maxHeight = Math.min(viewportHeight, 400); // Limit max height (e.g., 600px or viewport height)
				maxWidth = Math.min(maxHeight * videoAspectRatio, viewportWidth - 48);
			}

			// Set the player dimensions.
			playerContainerRef.style.width = `${maxWidth}px`;
			playerContainerRef.style.height = `${maxHeight}px`;
		});

		// Mark the component as mounted.
		mounted = true;
	});

	/**
	 * On component destroy, dispose of the Video.js player.
	 */
	onDestroy(() => {
		if (player) player.dispose();
	});

	/**
	 * Whether the component has been mounted.
	 * @type {boolean}
	 * @default false
	 */
	let mounted = false;

	/**
	 * Reference to the video element.
	 * @type {HTMLVideoElement}
	 */
	let playerRef: HTMLVideoElement;

	/**
	 * Reference to the player container element.
	 * @type {HTMLDivElement}
	 */
	let playerContainerRef: HTMLDivElement;
</script>

<div class="relative flex w-full justify-center">
	<!-- Video player container -->
	<div
		class="w-full {!mounted ? 'invisible absolute' : 'visible relative'}"
		bind:this={playerContainerRef}
	>
		<video
			bind:this={playerRef}
			class="video-js vjs-default-skin ring-company-primary rounded-lg ring-8"
			preload="auto"
			src={videoSrc}
			controls
		>
			<track kind="captions" />
			<source src={videoSrc + "#t=0.1"} type="video/mp4" />
		</video>
	</div>

	<!-- Skeleton loading placeholder -->
	{#if !mounted}
		<div
			class="h-48 w-full animate-pulse rounded-lg bg-gray-300 sm:h-64 sm:w-2/3 lg:h-96 lg:w-2/3"
		></div>
	{/if}
</div>
